<template>
  <DXForm
    :form="siteForm"
    :submit-text="site ? 'Save Changes' : 'Create Site'"
    :submit-loading-text="site ? 'Saving...' : 'Creating...'"
    @submit="handleSubmit"
  />
</template>

<script setup lang="ts">
import { watch } from "vue";
import { router } from "@inertiajs/vue3";
import { defineForm, DXForm } from "@omnitend/dashboard-for-laravel";
import type { FormFieldDefinition } from "@omnitend/dashboard-for-laravel";
import type { Site } from "../../types";

const props = defineProps<{
  site?: Site | null;
}>();

const isCreating = props.site === null || props.site === undefined;

// Create flow: the public URL is the seed — name derives from its domain,
// the slug derives from the name (not shown as a field; sent with the
// payload and derivable server-side too), and the editor URL derives from
// the slug (the add-site.sh convention: https://<slug>.admin.cloudship.co.uk,
// free under the wildcard DNS record). Each derivation stops once that field
// is hand-edited; clearing the editor URL means "no hosted editor".
// The edit form shows the slug — it's the site's identity, so changing it
// should be deliberate.
const fields: FormFieldDefinition[] = [
  {
    key: "public_url",
    type: "url",
    label: "Public URL",
    placeholder: "https://example.com",
    hint: isCreating ? "Start here — the other fields fill themselves in" : undefined,
    default: props.site?.public_url ?? "",
  },
  {
    key: "name",
    type: "text",
    label: "Name",
    placeholder: "Site name",
    required: true,
    default: props.site?.name ?? "",
  },
  ...(isCreating
    ? []
    : [
        {
          key: "slug",
          type: "text",
          label: "Slug",
          hint: "Unique identifier, used by sites:check-health --site= — change deliberately",
          required: true,
          default: props.site?.slug ?? "",
        } as FormFieldDefinition,
      ]),
  {
    key: "editor_url",
    type: "url",
    label: "Editor URL",
    placeholder: "https://site.admin.example.com",
    hint: "Editors live at <slug>.admin.cloudship.co.uk. Clear this if the site has no hosted editor — the public URL and its cert are still monitored",
    default: props.site?.editor_url ?? "",
  },
  {
    key: "repo_url",
    type: "text",
    label: "Repository",
    placeholder: "owner/repo",
    hint: "owner/repo is enough — it's normalized to the full SSH URL",
    required: true,
    default: props.site?.repo_url ?? "",
  },
  {
    key: "netlify_site_id",
    type: "text",
    label: "Netlify site",
    hint: "Netlify site name, used to link to the Netlify dashboard",
    default: props.site?.netlify_site_id ?? "",
  },
  {
    key: "notes",
    type: "textarea",
    label: "Notes",
    default: props.site?.notes ?? "",
  },
];

const siteForm = defineForm(fields as never);
// dfl form field values live on form.data (dfl's useForm is fetch-based,
// NOT Inertia's useForm).
const formData = siteForm.form.data as Record<string, string>;

const EDITOR_DOMAIN_SUFFIX = ".admin.cloudship.co.uk";

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// "https://www.greengrovepawsandpetal.co.uk/x" -> "greengrovepawsandpetal"
const domainLabel = (url: string): string => {
  let host = url.trim();
  try {
    host = new URL(host).hostname;
  } catch {
    host = host.replace(/^[a-z]+:\/\//i, "").split("/")[0] ?? "";
  }
  return host.replace(/^www\./i, "").split(".")[0] ?? "";
};

const deriveName = (publicUrl: string): string => {
  const label = domainLabel(publicUrl);
  return label === "" ? "" : label.charAt(0).toUpperCase() + label.slice(1);
};

const deriveEditorUrl = (slug: string): string =>
  slug === "" ? "" : `https://${slug}${EDITOR_DOMAIN_SUFFIX}`;

if (isCreating) {
  let nameManuallyEdited = false;
  let editorUrlManuallyEdited = false;

  watch(
    () => formData.public_url,
    (publicUrl) => {
      if (!nameManuallyEdited) {
        formData.name = deriveName(publicUrl);
      }
    },
  );
  watch(
    () => formData.name,
    (name) => {
      if (name !== deriveName(formData.public_url)) {
        nameManuallyEdited = true;
      }
      if (!editorUrlManuallyEdited) {
        formData.editor_url = deriveEditorUrl(slugify(name));
      }
    },
  );
  watch(
    () => formData.editor_url,
    (editorUrl) => {
      if (editorUrl !== deriveEditorUrl(slugify(formData.name))) {
        editorUrlManuallyEdited = true;
      }
    },
  );
}

// Empty optional strings should reach the API as nulls, and the create
// payload carries the derived slug. In dfl's form, `transform` is an option
// to post/put, not a chainable method.
const preparePayload = (data: Record<string, unknown>) => ({
  ...data,
  ...(isCreating ? { slug: slugify(String(data.name ?? "")) } : {}),
  editor_url: data.editor_url === "" ? null : data.editor_url,
  public_url: data.public_url === "" ? null : data.public_url,
  netlify_site_id: data.netlify_site_id === "" ? null : data.netlify_site_id,
  notes: data.notes === "" ? null : data.notes,
});

const handleSubmit = async () => {
  const options = {
    transform: preparePayload,
    // The controller responds with JSON to this fetch-based form (a
    // redirect would make fetch replay a PUT against the target); navigate
    // client-side (custard's pattern).
    onSuccess: () => router.visit("/sites"),
  };

  if (props.site) {
    await siteForm.form.put(`/sites/${props.site.id}`, options);
  } else {
    await siteForm.form.post("/sites", options);
  }
};
</script>
